import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	ReactNode,
} from "react";
import { createDefaultGpmData } from "../data/gpm";
import { createDefaultTdhData } from "../data/tdh";
import {
	SumpGpmHelp,
	SumpTdhHelp,
	ChartSeries,
	GpmData,
	GpmEntryMode,
	ChartDataResponse,
	ProductDataResult,
	PumpSizingFormData,
	SepticGpmHelp,
	SeriesFilterKey,
	SeriesFilters,
	StandardTdhHelp,
	TdhData,
	TdhEntryMode,
} from "../types/types";
import { emptySeriesFilters } from "../data/product";
import {
	fromSeriesViewState,
	normalizeSeriesMap,
	seriesLookupCodes,
	toSeriesViewState,
} from "../utils/helpers/productHelpers";
import { isGpmStepValid, calculateTotalFixtureUnits } from "../utils/helpers/gpmHelpers";
import { applyStepCalculations } from "../utils/formulas/applyStepCalculations";
import { applyTotalPipeLength, isTdhStepValid } from "../utils/helpers/tdhHelpers";
import { buildChartDataRequest } from "../utils/helpers/chartHelpers";
import { fetchChartData } from "../requests/chartDataRequest";
import { fetchProductData } from "../requests/productDataRequest";
import { loadSubmission, saveSubmission } from "../requests/submissionRequest";

type PumpSizingContextValue = {
	currentStep: number;
	totalSteps: number;
	formData: PumpSizingFormData;
	stepError: string;
	isNextLoading: boolean;
	chartData: ChartDataResponse | null;
	productData: ProductDataResult;
	/** Covers the chart and the product data behind it: both feed step 4. */
	isResultsLoading: boolean;
	resultsError: string;
	savedResultId: string;
	savedSubmissionUuid: string;
	/** True only after a save/update in this session, not after loading by ID. */
	resultsSaved: boolean;
	isSavingResults: boolean;
	saveResultsError: string;
	hiddenSeries: ReadonlySet<string>;
	seriesFilters: Record<string, SeriesFilters>;
	toggleSeriesCurve: (seriesCode: string) => void;
	setSeriesFilter: (
		seriesCode: string,
		key: SeriesFilterKey,
		value: string,
	) => void;
	loadResults: () => Promise<void>;
	setApplicationType: (value: PumpSizingFormData["applicationType"]) => void;
	setHazardousLocation: (value: boolean) => void;
	setSavedResultId: (value: string) => void;
	setGpmMode: (mode: GpmEntryMode) => void;
	setKnownGpm: (value: string) => void;
	updateSumpGpm: (updates: Partial<SumpGpmHelp>) => void;
	updateSepticGpm: (updates: Partial<SepticGpmHelp>) => void;
	setSewageFixtureUnit: (fixtureId: string, value: string) => void;
	setTdhMode: (mode: TdhEntryMode) => void;
	setKnownTdh: (value: string) => void;
	updateSumpTdh: (updates: Partial<SumpTdhHelp>) => void;
	updateStandardTdh: (updates: Partial<StandardTdhHelp>) => void;
	goToStep: (step: number) => void;
	nextStep: () => Promise<void>;
	prevStep: () => void;
	saveResults: () => Promise<boolean>;
	startAgain: () => void;
	canGoNext: boolean;
};

const defaultFormData: PumpSizingFormData = {
	applicationType: "sump",
	hazardousLocation: null,
	gpmData: createDefaultGpmData(),
	tdhData: createDefaultTdhData(),
};

const emptyProductData: ProductDataResult = {
	products: [],
	seriesUrls: {},
	seriesImages: {},
};

const PumpSizingContext = createContext<PumpSizingContextValue | null>(null);

type PumpSizingProviderProps = {
	children: ReactNode;
	totalSteps?: number;
};

export function PumpSizingProvider({
	children,
	totalSteps = 4,
}: PumpSizingProviderProps) {
	const [currentStep, setCurrentStep] = useState(1);
	const [formData, setFormData] = useState<PumpSizingFormData>(defaultFormData);
	const [stepError, setStepError] = useState("");
	const [isNextLoading, setIsNextLoading] = useState(false);
	const [chartData, setChartData] = useState<ChartDataResponse | null>(null);
	const [productData, setProductData] =
		useState<ProductDataResult>(emptyProductData);
	const [isResultsLoading, setIsResultsLoading] = useState(false);
	const [resultsError, setResultsError] = useState("");
	const [savedResultId, setSavedResultIdState] = useState("");
	const [savedSubmissionUuid, setSavedSubmissionUuid] = useState("");
	const [resultsSaved, setResultsSaved] = useState(false);
	const [isSavingResults, setIsSavingResults] = useState(false);
	const [saveResultsError, setSaveResultsError] = useState("");
	const [hiddenSeries, setHiddenSeries] = useState<ReadonlySet<string>>(
		new Set<string>(),
	);
	const [seriesFilters, setSeriesFilters] = useState<
		Record<string, SeriesFilters>
	>({});

	// Reset alongside the chart: the incoming series may differ entirely, which
	// also makes any "Results Saved" confirmation stale.
	const clearResults = useCallback(() => {
		setChartData(null);
		setProductData(emptyProductData);
		setResultsError("");
		setResultsSaved(false);
		setHiddenSeries(new Set<string>());
		setSeriesFilters({});
	}, []);

	const toggleSeriesCurve = useCallback((seriesCode: string) => {
		setHiddenSeries((previous) => {
			const next = new Set(previous);
			if (!next.delete(seriesCode)) {
				next.add(seriesCode);
			}
			return next;
		});
	}, []);

	const setSeriesFilter = useCallback(
		(seriesCode: string, key: SeriesFilterKey, value: string) => {
			setSeriesFilters((previous) => ({
				...previous,
				[seriesCode]: {
					...(previous[seriesCode] ?? emptySeriesFilters()),
					[key]: value,
				},
			}));
		},
		[],
	);

	const setApplicationType = useCallback(
		(value: PumpSizingFormData["applicationType"]) => {
			setStepError("");
			clearResults();
			setSavedSubmissionUuid("");
			setSaveResultsError("");
			if (value !== "saved") {
				setSavedResultIdState("");
			}
			setFormData((prev) => ({
				...prev,
				applicationType: value,
				hazardousLocation: null,
				gpmData: createDefaultGpmData(),
				tdhData: createDefaultTdhData(),
			}));
		},
		[clearResults],
	);

	const setHazardousLocation = useCallback(
		(value: boolean) => {
			setStepError("");
			clearResults();
			setFormData((prev) => ({ ...prev, hazardousLocation: value }));
		},
		[clearResults],
	);

	const setSavedResultId = useCallback((value: string) => {
		setStepError("");
		setSavedResultIdState(value);
	}, []);

	const updateGpmData = useCallback(
		(updater: (gpmData: GpmData) => GpmData) => {
			setStepError("");
			clearResults();
			setFormData((prev) => ({
				...prev,
				gpmData: updater(prev.gpmData),
			}));
		},
		[clearResults],
	);

	const setGpmMode = useCallback(
		(mode: GpmEntryMode) => {
			updateGpmData((gpmData) => ({ ...gpmData, mode }));
		},
		[updateGpmData],
	);

	const setKnownGpm = useCallback(
		(value: string) => {
			updateGpmData((gpmData) => ({
				...gpmData,
				knownGpm: value,
			}));
		},
		[updateGpmData],
	);

	const updateSumpGpm = useCallback(
		(updates: Partial<SumpGpmHelp>) => {
			updateGpmData((gpmData) => ({
				...gpmData,
				sump: { ...gpmData.sump, ...updates },
			}));
		},
		[updateGpmData],
	);

	const updateSepticGpm = useCallback(
		(updates: Partial<SepticGpmHelp>) => {
			updateGpmData((gpmData) => ({
				...gpmData,
				septic: { ...gpmData.septic, ...updates },
			}));
		},
		[updateGpmData],
	);

	const setSewageFixtureUnit = useCallback(
		(fixtureId: string, value: string) => {
			updateGpmData((gpmData) => {
				const fixtureUnits = {
					...gpmData.sewage.fixtureUnits,
					[fixtureId]: value,
				};

				return {
					...gpmData,
					sewage: {
						fixtureUnits,
						totalFixtureUnits: calculateTotalFixtureUnits(fixtureUnits),
					},
				};
			});
		},
		[updateGpmData],
	);

	const updateTdhData = useCallback(
		(updater: (tdhData: TdhData) => TdhData) => {
			setStepError("");
			clearResults();
			setFormData((prev) => ({
				...prev,
				tdhData: updater(prev.tdhData),
			}));
		},
		[clearResults],
	);

	const setTdhMode = useCallback(
		(mode: TdhEntryMode) => {
			updateTdhData((tdhData) => ({ ...tdhData, mode }));
		},
		[updateTdhData],
	);

	const setKnownTdh = useCallback(
		(value: string) => {
			updateTdhData((tdhData) => ({ ...tdhData, knownTdh: value }));
		},
		[updateTdhData],
	);

	const updateSumpTdh = useCallback(
		(updates: Partial<SumpTdhHelp>) => {
			setStepError("");
			clearResults();
			setFormData((prev) => ({
				...prev,
				tdhData: {
					...prev.tdhData,
					sump: applyTotalPipeLength({
						...prev.tdhData.sump,
						...updates,
					}),
				},
			}));
		},
		[clearResults],
	);

	const updateStandardTdh = useCallback(
		(updates: Partial<StandardTdhHelp>) => {
			setStepError("");
			clearResults();
			setFormData((prev) => ({
				...prev,
				tdhData: {
					...prev.tdhData,
					standard: applyTotalPipeLength({
						...prev.tdhData.standard,
						...updates,
					}),
				},
			}));
		},
		[clearResults],
	);

	const fetchProductsForSeries = useCallback(
		async (series: ChartSeries[]): Promise<ProductDataResult> => {
			const codes = series.map((item) => item.series_code).filter(Boolean);

			if (codes.length === 0) {
				return emptyProductData;
			}

			const result = await fetchProductData(seriesLookupCodes(codes));

			return {
				products: result.products,
				seriesUrls: normalizeSeriesMap(result.seriesUrls),
				seriesImages: normalizeSeriesMap(result.seriesImages),
			};
		},
		[],
	);

	// Chart and product data are loaded back to back under a single flag so
	// step 4 shows one spinner instead of two in sequence.
	const loadResults = useCallback(async () => {
		if (isResultsLoading) {
			return;
		}

		setIsResultsLoading(true);
		setResultsError("");

		try {
			const body = buildChartDataRequest(formData);

			const data = await fetchChartData(body);
			setChartData(data);

			try {
				setProductData(await fetchProductsForSeries(data.series));
			} catch (error) {
				// The curves are still usable without the cards, so the chart stays.
				setProductData(emptyProductData);
				setResultsError(
					error instanceof Error
						? error.message
						: "Unable to load product details. Please try again.",
				);
			}
		} catch (error) {
			setChartData(null);
			setProductData(emptyProductData);
			setResultsError(
				error instanceof Error
					? error.message
					: "Unable to load results. Please try again.",
			);
		} finally {
			setIsResultsLoading(false);
		}
	}, [fetchProductsForSeries, formData, isResultsLoading]);

	const goToStep = useCallback(
		(step: number) => {
			if (step >= 1 && step <= totalSteps) {
				setStepError("");
				setCurrentStep(step);
			}
		},
		[totalSteps],
	);

	const nextStep = useCallback(async () => {
		if (isNextLoading) {
			return;
		}

		setStepError("");
		setIsNextLoading(true);

		try {
			if (currentStep === 1 && formData.applicationType === "saved") {
				const { uuid, payload } = await loadSubmission(savedResultId.trim());
				const restored = fromSeriesViewState(payload.seriesView);
				// Fetched here too so step 4 opens with the cards already in place.
				const products = await fetchProductsForSeries(
					payload.chartData?.series ?? [],
				);

				setFormData(payload.formData);
				setChartData(payload.chartData);
				setProductData(products);
				setResultsError("");
				setHiddenSeries(restored.hiddenSeries);
				setSeriesFilters(restored.filtersBySeries);
				setSavedSubmissionUuid(uuid);
				// Loading by ID is not a save, so the confirmation panel stays hidden.
				setResultsSaved(false);
				setSaveResultsError("");
				setCurrentStep(totalSteps);
				return;
			}

			const nextFormData = await applyStepCalculations(formData, currentStep);

			setFormData(nextFormData);
			setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
		} catch (error) {
			setStepError(
				error instanceof Error
					? error.message
					: "Something went wrong. Please try again.",
			);
		} finally {
			setIsNextLoading(false);
		}
	}, [
		currentStep,
		fetchProductsForSeries,
		formData,
		isNextLoading,
		savedResultId,
		totalSteps,
	]);

	const prevStep = useCallback(() => {
		setStepError("");
		// Leaving step 4 dismisses the save confirmation; the header keeps the ID.
		setResultsSaved(false);
		setCurrentStep((prev) => Math.max(prev - 1, 1));
	}, []);

	const saveResults = useCallback(async () => {
		if (isSavingResults) {
			return false;
		}

		setSaveResultsError("");
		setIsSavingResults(true);

		try {
			const uuid = await saveSubmission(
				{
					formData,
					chartData,
					seriesView: toSeriesViewState(hiddenSeries, seriesFilters),
				},
				savedSubmissionUuid || undefined,
			);
			setSavedSubmissionUuid(uuid);
			setResultsSaved(true);
			return true;
		} catch (error) {
			setSaveResultsError(
				error instanceof Error
					? error.message
					: "Unable to save results. Please try again.",
			);
			return false;
		} finally {
			setIsSavingResults(false);
		}
	}, [
		chartData,
		formData,
		hiddenSeries,
		isSavingResults,
		savedSubmissionUuid,
		seriesFilters,
	]);

	const startAgain = useCallback(() => {
		setStepError("");
		setIsNextLoading(false);
		setSavedResultIdState("");
		setSavedSubmissionUuid("");
		setSaveResultsError("");
		setIsSavingResults(false);
		clearResults();
		setFormData(defaultFormData);
		setCurrentStep(1);
	}, [clearResults]);

	const canGoNext = useMemo(() => {
		switch (currentStep) {
			case 1:
				if (formData.applicationType === "saved") {
					return savedResultId.trim() !== "";
				}
				if (formData.applicationType === "email") {
					return true;
				}
				return (
					formData.applicationType !== null &&
					formData.hazardousLocation !== null
				);
			case 2:
				return isGpmStepValid(formData);
			case 3:
				return isTdhStepValid(formData);
			default:
				return true;
		}
	}, [currentStep, formData, savedResultId]);

	const value = useMemo(
		() => ({
			currentStep,
			totalSteps,
			formData,
			stepError,
			isNextLoading,
			chartData,
			productData,
			isResultsLoading,
			resultsError,
			savedResultId,
			savedSubmissionUuid,
			resultsSaved,
			isSavingResults,
			saveResultsError,
			hiddenSeries,
			seriesFilters,
			toggleSeriesCurve,
			setSeriesFilter,
			loadResults,
			setApplicationType,
			setHazardousLocation,
			setSavedResultId,
			setGpmMode,
			setKnownGpm,
			updateSumpGpm,
			updateSepticGpm,
			setSewageFixtureUnit,
			setTdhMode,
			setKnownTdh,
			updateSumpTdh,
			updateStandardTdh,
			goToStep,
			nextStep,
			prevStep,
			saveResults,
			startAgain,
			canGoNext,
		}),
		[
			currentStep,
			totalSteps,
			formData,
			stepError,
			isNextLoading,
			chartData,
			productData,
			isResultsLoading,
			resultsError,
			savedResultId,
			savedSubmissionUuid,
			resultsSaved,
			isSavingResults,
			saveResultsError,
			hiddenSeries,
			seriesFilters,
			toggleSeriesCurve,
			setSeriesFilter,
			loadResults,
			setApplicationType,
			setHazardousLocation,
			setSavedResultId,
			setGpmMode,
			setKnownGpm,
			updateSumpGpm,
			updateSepticGpm,
			setSewageFixtureUnit,
			setTdhMode,
			setKnownTdh,
			updateSumpTdh,
			updateStandardTdh,
			goToStep,
			nextStep,
			prevStep,
			saveResults,
			startAgain,
			canGoNext,
		],
	);

	return (
		<PumpSizingContext.Provider value={value}>
			{children}
		</PumpSizingContext.Provider>
	);
}

export function usePumpSizing() {
	const context = useContext(PumpSizingContext);
	if (!context) {
		throw new Error("usePumpSizing must be used within PumpSizingProvider");
	}
	return context;
}
